import { IManifest } from "@kurtosys/ksys-app-template/dist/appsManager/models";

export interface LogOptions {
	message: string;
	detail?: any;
	configKey?: string;
	additionalContext?: string;
}

export type TLogType = 'debug' | 'info' | 'success' | 'warning' | 'error';

export interface BaseLogOptions extends LogOptions {
	type: TLogType;
	color: string;
}

export function log(options: BaseLogOptions, manifest: IManifest) {
	const { type, color, message, detail, configKey, additionalContext } = options;
	const app = manifest.packageName;
	const configContext = configKey ? `.${configKey}` : '';
	const extraContext = additionalContext ? `.${additionalContext}` : '';
	const args = [
		`%c${type}: %c${app}%c${configContext}${extraContext}%c: ${message}`,
		`color: ${color}; font-weight: 500; font-size: 1.3em; text-transform: uppercase;`,
		'color: #FFFFFF; font-weight: 900; text-transform: uppercase;',
		'color: #00FFDD; font-size: 1.1em;',
		'color: #FFFFFF; font-weight: normal; font-size: 0.9em;',
	];
	if (detail) {
		console.group(...args);
		console.log(detail);
		console.groupEnd();
	} else {
		console.log(...args);
	}
}

export function warning(options: LogOptions, manifest: IManifest) {
	log(
		{
			...options,
			type: 'warning',
			color: '#eed202',
		},
		manifest,
	);
}

export function error(options: LogOptions, manifest: IManifest) {
	log(
		{
			...options,
			type: 'error',
			color: '#FF9494',
		},
		manifest,
	);
}

export function info(options: LogOptions, manifest: IManifest) {
	log(
		{
			...options,
			type: 'info',
			color: '#77CCFF',
		},
		manifest,
	);
}

export function success(options: LogOptions, manifest: IManifest) {
	log(
		{
			...options,
			type: 'info',
			color: '#00FF00',
		},
		manifest,
	);
}

export function debug(options: LogOptions, manifest: IManifest) {
	log(
		{
			...options,
			type: 'debug',
			color: '#FF00FF',
		},
		manifest,
	);
}

/**
 * A map of log group IDs to colors.
 * Used to ensure that the same ID always gets the same color.
 */
const logColorMap = new Map<string, string>();

/**
 * Logs a group of messages with a specified ID to a random color.
 * If the ID has not been used before, a random color will be generated and associated with it.
 * @param id The ID of the log group.
 * @param name The name of the log group.
 * @param args The messages to log.
 */
export function logGroup(id = 'default', name: string, ...args: Array<any>) {
	if (!logColorMap.has(id)) {
		logColorMap.set(id, getRandomColor());
	}
	const workingColor = logColorMap.get(id);
	console.log(
		`%c [Log ${id}] ${name}`,
		`background: ${workingColor}; color: ${getContrastColor(workingColor)}`,
		args,
	);
}

/**
 * Generates a random hex color code.
 * @returns A random hex color code.
 */
export function getRandomColor() {
	const letters = '0123456789ABCDEF';
	let color = '#';
	for (let i = 0; i < 6; i++) {
		color += letters[Math.floor(Math.random() * 16)];
	}
	return color;
}

/**
 * Calculates the contrast color (black or white) for a given hex color code.
 * @param hexColor The hex color code to calculate the contrast color for.
 * @returns The contrast color (black or white) as a hex color code.
 */
export function getContrastColor(hexColor: string) {
	const threshold = 130;
	const rgbColor = parseInt(hexColor.substring(1), 16);
    /* tslint:disable:no-bitwise */
	const r = (rgbColor >> 16) & 0xff;
	const g = (rgbColor >> 8) & 0xff;
	const b = rgbColor & 0xff;
	const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
	return luminance > threshold ? '#000' : '#fff';
}



// WEBPACK FOOTER //
// ./src/utils/log.ts